import { Button, MenuIcon, ProfileIcon, Select, SelectChip, SelectOptionType } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { AppLayoutContext } from '../../contexts/AppLayoutContextProvider';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useSchoolYearSelect } from '../../hooks/useSchoolYearSelect';
import { HelpMenu } from '../HelpMenu/HelpMenu';
import styles from './AppHeader.module.scss';

export const AppHeader = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();
  const isMobile = useIsMobile();
  const { setMobileMenuIsOpen } = useContext(AppLayoutContext);
  const schoolYear = useSchoolYearSelect();

  const profile = pimAuthClaims.getProfile();
  const name = ((profile?.firstName ?? '') + ' ' + (profile?.lastName ?? '')).trim() || 'unknown person';
  const school = profile?.organization?.name ?? '';

  if (isMobile) {
    return (
      <header className={styles['app-header']} data-cy='headerWrapper'>
        <div className={styles['mobile-bar']}>
          <button
            type='button'
            className={styles.burger}
            aria-label={t('navigation.openMenu')}
            onClick={() => setMobileMenuIsOpen(true)}
          >
            <MenuIcon />
          </button>
          <span className={styles.school}>{school}</span>
          <SelectChip
            ariaLabel={`${t('common.schoolYear')}: ${schoolYear.selectedOption.label ?? ''}`}
            isDisabled={schoolYear.isDisabled}
            label={String(schoolYear.selectedShortName)}
            options={schoolYear.options}
            value={schoolYear.selectedOption.value as string}
            onSelect={schoolYear.selectSchoolYear}
          />
        </div>
      </header>
    );
  }

  return (
    <header className={styles['app-header']} data-cy='headerWrapper'>
      <div className={styles.container}>
        <div className={styles.left}>
          <span className={styles.label}>{t('common.schoolYear')}</span>
          <Select
            className={styles.select}
            name='year'
            isMulti={false}
            dense
            disabled={schoolYear.isDisabled}
            onChange={(option) => {
              const opt = option as SingleValue<SelectOptionType>;
              schoolYear.selectSchoolYear(opt?.value as string);
            }}
            options={schoolYear.options}
            value={schoolYear.selectedOption}
          />
        </div>
        <div className={styles.center}>{school}</div>
        <div className={styles.right}>
          <HelpMenu className={styles.about} />
          <Button
            name='profile'
            className='ml-2'
            hierarchy='tertiary'
            icon={<ProfileIcon />}
            onClick={() => navigate(`/${t('routes.profile.slug')}`)}
          >
            {profile ? <p>{name}</p> : <p>Logged-in, but no profile</p>}
          </Button>
        </div>
      </div>
    </header>
  );
});
